import { useQuery } from '@tanstack/react-query';
import { X, Loader2, MapPin, RefreshCw, CheckCircle2, Package, Truck, Home, Copy, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { STATUS_LABEL, STATUS_COLOR } from './constants';
import { CancelDispatchPanel } from '@/components/order/CancelDispatchPanel';
import { useAuth } from '@/hooks/useAuth';

interface TrackEvent {
  id: number; statusId: number; eventDateTime: string; statusMessage?: string;
  /** The courier's own free-text place string. This is the field that is actually populated. */
  location?: string;
  scanCity?: string; scanState?: string; scanPincode?: string;
  receiverName?: string; receiverPhone?: string;
}
/** The delivery appointment booked against this consignment, sent by `GET /orders/:id`. */
interface TrackAppointment {
  id: number;
  appointmentRef?: string | null;
  appointmentDate?: string | null;
  appointmentTime?: string | null;
  poNumber?: string | null;
  pending?: boolean;
}

interface OrderTrack {
  appointment?: TrackAppointment | null;
  id: string; orderNo?: string; awbNo?: string; lrNo?: string; orderType?: 'B2C' | 'B2B';
  statusId: number; edd?: string; lastEventDate?: string; deliveredAt?: string;
  customerName?: string; customerCity?: string; customerState?: string; customerPincode?: string;
  pickupAddress?: string; pickupPincode?: string;
  courier?: { name?: string; addressLabelName?: string } | null;
  trackingEvents?: TrackEvent[];
}

/**
 * Where a scan happened, as the courier reported it.
 *
 * `location` is the courier's own free-text place and is set on ~90% of events; the structured
 * scanCity/scanState/scanPincode columns are set on under 2%, because almost no carrier feed
 * separates them. The drawer previously rendered ONLY those structured columns, so nine out of ten
 * scans showed no location at all even though the string was sitting in the payload.
 */
function placeOf(ev: TrackEvent): string {
  const structured = [ev.scanCity, ev.scanState, ev.scanPincode].filter(Boolean).join(', ');
  const loc = (ev.location ?? '').trim();
  if (!loc) return structured;
  if (!structured) return loc;
  // Both present: prefer the courier's string, and only append the structured part when it adds
  // something the string does not already contain.
  return structured.toLowerCase().includes(loc.toLowerCase()) ? structured : loc;
}

/** Terminal states — used to decide whether the journey bar reads as finished. */
const DELIVERED = [8, 19];
const RTO = [10, 11, 12, 14, 16, 18, 34, 35, 36, 37, 38];

export function TrackingDrawer({ orderId, onClose }: { orderId: string | number | null; onClose: () => void }) {
  const open = orderId != null;
  const navigate = useNavigate();
  const isStaffViewer = useAuth((s) => s.user)?.kind !== 'merchant';

  const { data, isFetching, refetch } = useQuery<OrderTrack>({
    queryKey: ['order-track', orderId],
    enabled: open,
    queryFn: async () => (await api.get<OrderTrack>(`/orders/${orderId}`)).data,
  });

  const events = (data?.trackingEvents ?? []).slice()
    .sort((a, b) => new Date(b.eventDateTime).getTime() - new Date(a.eventDateTime).getTime());
  const currentEventId = events[0]?.id;
  const ref = data?.orderType === 'B2B' ? data?.lrNo : data?.awbNo;
  const isDelivered = data ? DELIVERED.includes(data.statusId) : false;
  const isRto = data ? RTO.includes(data.statusId) : false;

  // Elapsed time in the network — the question people open this drawer to answer.
  const first = events.length ? events[events.length - 1] : null;
  const last = events.length ? events[0] : null;
  const days = first && last
    ? Math.max(0, Math.round((new Date(last.eventDateTime).getTime() - new Date(first.eventDateTime).getTime()) / 86400_000))
    : null;

  const copy = (v?: string) => {
    if (!v) return;
    navigator.clipboard?.writeText(v).then(() => toast.success('Copied'), () => {});
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px] transition-opacity ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-slate-50 shadow-2xl transition-transform duration-300 flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* ── Header: the AWB is the thing people came for, so it leads and is copyable ────────── */}
        <div className="bg-white border-b border-slate-200 px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                {data?.orderType === 'B2B' ? 'LR Number' : 'AWB Number'}
              </div>
              <button
                onClick={() => copy(ref)}
                title="Copy"
                className="font-mono text-base font-semibold text-slate-900 flex items-center gap-1.5 hover:text-primary">
                {ref ?? (isFetching ? '…' : '—')}
                {ref && <Copy size={12} className="text-slate-400" />}
              </button>
              <div className="text-xs text-slate-500 truncate mt-0.5">
                {data?.orderNo ? `#${data.orderNo}` : ''}
                {data?.courier?.addressLabelName || data?.courier?.name ? ` · ${data.courier.addressLabelName || data.courier.name}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => refetch()} disabled={isFetching} title="Refresh"
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 disabled:opacity-50">
                <RefreshCw size={15} className={isFetching ? 'animate-spin' : ''} />
              </button>
              <button onClick={onClose} title="Close"
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={18} /></button>
            </div>
          </div>

          {/* Current status as a full-width band — it is the headline, not a chip among chips. */}
          {data && (
            <div className={`mt-3 rounded-lg px-3 py-2 border ${STATUS_COLOR[data.statusId] ?? 'bg-slate-100 text-slate-700 border-slate-200'}`}>
              <div className="text-sm font-semibold">{STATUS_LABEL[data.statusId] ?? `Status ${data.statusId}`}</div>
              {last && (
                <div className="text-[11px] opacity-80 mt-0.5">
                  {placeOf(last) ? `${placeOf(last)} · ` : ''}
                  {new Date(last.eventDateTime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          )}

          {/* The delivery appointment, when the consignee books slots. The number is what the
              consignee's warehouse asks for at the gate, so it sits with the status rather than
              being something to go and look up in another screen. */}
          {data?.appointment && (
            <div className="mt-2 rounded-lg px-3 py-2 border border-indigo-200 bg-indigo-50 text-indigo-900">
              <div className="text-[11px] uppercase tracking-wide opacity-70">Appointment</div>
              <div className="text-sm font-mono font-semibold">
                {data.appointment.appointmentRef || `#${data.appointment.id}`}
                {data.appointment.pending && <span className="ml-2 font-sans text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">date pending</span>}
              </div>
              <div className="text-[11px] opacity-80">
                {data.appointment.appointmentDate
                  ? `${new Date(data.appointment.appointmentDate).toLocaleDateString('en-IN')}${data.appointment.appointmentTime ? ` · ${data.appointment.appointmentTime}` : ''}`
                  : 'Slot not booked yet'}
                {data.appointment.poNumber ? ` · PO ${data.appointment.poNumber}` : ''}
              </div>
            </div>
          )}

          {/* ── Journey bar: origin → transit → destination ───────────────────────────────────── */}
          {data && (
            <div className="mt-3 flex items-center gap-1.5">
              {[
                { icon: <Package size={12} />, label: 'Picked', done: events.some(e => e.statusId >= 3) },
                { icon: <Truck size={12} />, label: isRto ? 'Returning' : 'In transit', done: events.some(e => e.statusId >= 5) },
                { icon: <Home size={12} />, label: isRto ? 'Returned' : 'Delivered', done: isDelivered || RTO.slice(1).includes(data.statusId) },
              ].map((s, i, arr) => (
                <div key={s.label} className="flex items-center gap-1.5 flex-1 last:flex-none">
                  <div className={`flex items-center gap-1 text-[10px] px-1.5 py-1 rounded ${s.done ? (isRto ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800') : 'bg-slate-100 text-slate-400'}`}>
                    {s.icon}{s.label}
                  </div>
                  {i < arr.length - 1 && <div className={`h-0.5 flex-1 rounded ${arr[i + 1].done ? (isRto ? 'bg-amber-300' : 'bg-emerald-300') : 'bg-slate-200'}`} />}
                </div>
              ))}
            </div>
          )}

          {/* Facts worth having without leaving the drawer. */}
          {data && (
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <Fact label="Scans" value={events.length ? String(events.length) : '—'} />
              <Fact label="In network" value={days == null ? '—' : `${days}d`} />
              <Fact label={isDelivered ? 'Delivered' : 'EDD'}
                value={
                  isDelivered && data.deliveredAt
                    ? new Date(data.deliveredAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                    : data.edd
                      ? new Date(data.edd).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                      : '—'
                } />
            </div>
          )}

          {/* Where it is going. */}
          {(data?.customerCity || data?.customerName) && (
            <div className="mt-3 text-xs text-slate-600 flex items-start gap-1.5">
              <MapPin size={12} className="mt-0.5 shrink-0 text-slate-400" />
              <span className="truncate">
                <b className="text-slate-800">{data?.customerName}</b>
                {data?.customerCity ? ` · ${[data.customerCity, data.customerState, data.customerPincode].filter(Boolean).join(', ')}` : ''}
              </span>
            </div>
          )}
        </div>

        {/* ── Timeline ─────────────────────────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {data && <div className="-mt-4 mb-3"><CancelDispatchPanel order={data} canEdit={isStaffViewer} /></div>}
          {isFetching && !data ? (
            <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-primary" /></div>
          ) : events.length === 0 ? (
            <div className="text-sm text-slate-400 py-16 text-center">
              No tracking events yet.
              <div className="text-xs text-slate-300 mt-1">Scans appear here once the courier reports them.</div>
            </div>
          ) : (
            <ol className="relative space-y-3">
              {/* One continuous rail behind the dots, rather than a border on every <li>. */}
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" aria-hidden />
              {events.map((ev) => {
                const dot = (STATUS_COLOR[ev.statusId]?.split(' ')[0] ?? 'bg-slate-300').replace('text-', 'bg-');
                const isCurrent = ev.id === currentEventId;
                const place = placeOf(ev);
                return (
                  <li key={ev.id} className="relative pl-7">
                    <span className="absolute left-0 top-1.5 flex h-3.5 w-3.5 items-center justify-center">
                      {isCurrent && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${dot}`} />}
                      <span className={`relative inline-flex rounded-full ${isCurrent ? 'h-3.5 w-3.5 ring-2 ring-white' : 'h-2.5 w-2.5'} ${dot}`} />
                    </span>
                    {/* Two columns: WHAT happened on the left, WHEN and WHERE on the right.
                        Everything used to stack down the left edge, leaving the right half of every
                        card empty — and date and place are exactly the pair the eye scans down when
                        following a shipment, so they belong in their own aligned column. */}
                    <div className={`rounded-lg border p-2.5 ${isCurrent ? 'bg-white border-slate-300 shadow-sm' : 'bg-white/70 border-slate-200'}`}>
                      <div className="flex items-start gap-3">
                        {/* LEFT — the event itself */}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-800 leading-snug">
                            {STATUS_LABEL[ev.statusId] ?? `Status ${ev.statusId}`}
                          </div>
                          {ev.statusMessage && ev.statusMessage !== place && (
                            <div className="text-xs text-slate-500 mt-0.5 break-words">{ev.statusMessage}</div>
                          )}
                        </div>

                        {/* RIGHT — when and where, right-aligned so the column reads as one strip */}
                        <div className="w-[45%] shrink-0 text-right">
                          <div className="text-[11px] font-medium text-slate-600 tabular-nums">
                            {new Date(ev.eventDateTime).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                          </div>
                          <div className="text-[11px] text-slate-400 tabular-nums">
                            {new Date(ev.eventDateTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          {place && (
                            <div className="text-[11px] text-slate-600 mt-1 flex items-start justify-end gap-1 break-words">
                              <MapPin size={10} className="mt-[3px] shrink-0 text-slate-400" />
                              <span className="text-right leading-snug">{place}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {ev.receiverName && (
                        <div className="text-xs text-emerald-700 mt-2 flex items-center gap-1 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-1">
                          <CheckCircle2 size={11} /> Received by {ev.receiverName}{ev.receiverPhone ? ` · ${ev.receiverPhone}` : ''}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Full order detail is one click away rather than a dead end. */}
        {data && (
          <div className="border-t border-slate-200 bg-white px-4 py-2.5">
            <button
              onClick={() => { onClose(); navigate(`/orders-${(data.orderType ?? 'B2C').toLowerCase()}/${data.id}`); }}
              className="w-full text-sm text-primary font-medium flex items-center justify-center gap-1.5 py-1.5 rounded-lg hover:bg-primary/5">
              Open full order <ExternalLink size={13} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-slate-800">{value}</div>
    </div>
  );
}
